import { useCallback, useEffect, useState } from "react";
import { CheckCircle2, XCircle, Clock, Inbox, Eye, LockKeyhole, Trash2, LogOut, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import PageHero from "../components/PageHero";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { formatDate } from "../components/StoryCard";
import { adminLogin, adminListStories, adminSetStatus, adminDeleteStory, getAdminKey, setAdminKey, errorMessage } from "../lib/api";

const STATUS_STYLE = {
  pending: "bg-amber-100 text-amber-800",
  approved: "bg-[#4BAF47]/15 text-[#2f7d2c]",
  rejected: "bg-red-100 text-red-700",
};

export default function Admin() {
  const [authed, setAuthed] = useState(!!getAdminKey());
  const [pw, setPw] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setItems(await adminListStories());
    } catch (e) {
      if (e?.response?.status === 401) {
        setAdminKey("");
        setAuthed(false);
        toast.error("Session expired. Please sign in again.");
      } else toast.error(errorMessage(e, "Could not load submissions."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authed) load();
  }, [authed, load]);

  const login = async (e) => {
    e.preventDefault();
    try {
      await adminLogin(pw);
      setAdminKey(pw);
      setAuthed(true);
      setPw("");
    } catch {
      toast.error("Incorrect password.");
    }
  };

  const logout = () => {
    setAdminKey("");
    setAuthed(false);
    setItems([]);
  };

  const setStatus = async (id, status) => {
    try {
      const updated = await adminSetStatus(id, status);
      setItems((list) => list.map((s) => (s.id === id ? updated : s)));
      toast.success(status === "approved" ? "Story approved and published." : "Story rejected.");
      setPreview(null);
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this story permanently?")) return;
    try {
      await adminDeleteStory(id);
      setItems((list) => list.filter((s) => s.id !== id));
      toast.success("Story deleted.");
      setPreview(null);
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  if (!authed) {
    return (
      <section className="min-h-[70vh] flex items-center justify-center bg-[#F8F7F0] px-5">
        <form onSubmit={login} className="bg-white rounded-xl p-10 w-full max-w-md shadow-[0_30px_60px_-30px_rgba(36,35,29,0.3)] border border-[#ebe9df]">
          <span className="h-14 w-14 rounded-lg bg-[#4BAF47] text-white flex items-center justify-center"><LockKeyhole size={26} /></span>
          <h1 className="text-3xl font-extrabold mt-6">Editorial Login</h1>
          <p className="text-[#878680] mt-2 text-sm">Sign in to review submitted stories.</p>
          <Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Password" className="h-12 mt-6 focus-visible:ring-[#4BAF47] focus-visible:ring-offset-0" data-testid="admin-password" />
          <button type="submit" className="btn-green w-full justify-center mt-4" data-testid="admin-login">Sign in</button>
        </form>
      </section>
    );
  }

  const groups = {
    pending: items.filter((s) => s.status === "pending"),
    approved: items.filter((s) => s.status === "approved"),
    rejected: items.filter((s) => s.status === "rejected"),
  };

  const Row = ({ s }) => (
    <div className="bg-white rounded-xl border border-[#ebe9df] p-5 flex flex-col md:flex-row md:items-center gap-5">
      <div className="h-20 w-28 rounded-md bg-[#F8F7F0] overflow-hidden flex-shrink-0 flex items-center justify-center">
        {s.image ? <img src={s.image} alt="" className="h-full w-full object-cover" /> : <Inbox className="text-[#c9c8c0]" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <span className={`text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${STATUS_STYLE[s.status]}`}>{s.status}</span>
          <span className="text-xs text-[#878680]">{formatDate(s.created_at)}</span>
        </div>
        <h3 className="font-extrabold text-lg mt-1 truncate">{s.title}</h3>
        <p className="text-sm text-[#878680] truncate">{s.author.name} · {s.author.email} · {s.categories[0]}</p>
      </div>
      <div className="flex gap-2 flex-shrink-0">
        <button onClick={() => setPreview(s)} className="h-10 px-3 rounded-md border border-[#e4e2d6] text-sm font-semibold inline-flex items-center gap-1.5 hover:bg-[#F8F7F0]"><Eye size={15} /> View</button>
        {s.status !== "approved" && (
          <button onClick={() => setStatus(s.id, "approved")} className="h-10 px-3 rounded-md bg-[#4BAF47] text-white text-sm font-semibold inline-flex items-center gap-1.5 hover:bg-[#3f9a3c]" data-testid={`approve-${s.id}`}><CheckCircle2 size={15} /> Approve</button>
        )}
        {s.status !== "rejected" && (
          <button onClick={() => setStatus(s.id, "rejected")} className="h-10 px-3 rounded-md border border-red-200 text-red-600 text-sm font-semibold inline-flex items-center gap-1.5 hover:bg-red-50" data-testid={`reject-${s.id}`}><XCircle size={15} /> Reject</button>
        )}
        <button onClick={() => remove(s.id)} aria-label="Delete" className="h-10 w-10 rounded-md border border-[#e4e2d6] text-[#878680] inline-flex items-center justify-center hover:bg-[#F8F7F0] hover:text-red-600"><Trash2 size={15} /></button>
      </div>
    </div>
  );

  return (
    <>
      <PageHero eyebrow="Editorial" title="Story Submissions" subtitle="Review, approve or reject stories submitted through the website. Approved stories appear instantly in Discover Stories; seeded stories can be managed here too." />
      <section className="bg-[#F8F7F0] min-h-[50vh]">
        <div className="max-w-[1100px] mx-auto px-5 lg:px-8 py-14">
          <div className="flex justify-end gap-2 mb-6">
            <button onClick={load} disabled={loading} className="h-10 px-4 rounded-md border border-[#e4e2d6] bg-white text-sm font-semibold inline-flex items-center gap-2 hover:bg-[#F8F7F0] disabled:opacity-60"><RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh</button>
            <button onClick={logout} className="h-10 px-4 rounded-md border border-[#e4e2d6] bg-white text-sm font-semibold inline-flex items-center gap-2 hover:bg-[#F8F7F0]" data-testid="admin-logout"><LogOut size={14} /> Sign out</button>
          </div>
          <div className="grid grid-cols-3 gap-4 mb-10">
            {[
              { k: "pending", icon: Clock, label: "Pending" },
              { k: "approved", icon: CheckCircle2, label: "Approved" },
              { k: "rejected", icon: XCircle, label: "Rejected" },
            ].map(({ k, icon: Icon, label }) => (
              <div key={k} className="bg-white rounded-xl border border-[#ebe9df] p-5">
                <Icon size={20} className="text-[#4BAF47]" />
                <p className="text-3xl font-extrabold mt-2">{groups[k].length}</p>
                <p className="text-sm text-[#878680]">{label}</p>
              </div>
            ))}
          </div>

          <Tabs defaultValue="pending">
            <TabsList className="bg-white border border-[#ebe9df] h-12 p-1">
              {["pending", "approved", "rejected"].map((k) => (
                <TabsTrigger key={k} value={k} className="capitalize px-5 data-[state=active]:bg-[#4BAF47] data-[state=active]:text-white">{k} ({groups[k].length})</TabsTrigger>
              ))}
            </TabsList>
            {["pending", "approved", "rejected"].map((k) => (
              <TabsContent key={k} value={k} className="space-y-4 mt-6">
                {groups[k].length === 0 ? (
                  <div className="text-center py-16 text-[#878680] bg-white rounded-xl border border-dashed border-[#e4e2d6]">
                    <Inbox size={36} className="mx-auto mb-3 text-[#c9c8c0]" /> No {k} stories.
                  </div>
                ) : groups[k].map((s) => <Row key={s.id} s={s} />)}
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </section>

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          {preview && (
            <>
              <DialogHeader>
                <DialogTitle className="text-2xl font-extrabold pr-6">{preview.title}</DialogTitle>
              </DialogHeader>
              {preview.image && <img src={preview.image} alt="" className="w-full h-56 object-cover rounded-md" />}
              <p className="text-sm text-[#878680]">By {preview.author.name} ({preview.author.email}) · {preview.categories.join(", ")}</p>
              {preview.author.bio && <p className="text-sm italic text-[#5f5e58]">{preview.author.bio}</p>}
              <div className="text-[15px] leading-relaxed whitespace-pre-line text-[#3d3c36]">{preview.content}</div>
              <div className="flex gap-3 pt-2">
                {preview.status !== "approved" && <button onClick={() => setStatus(preview.id, "approved")} className="btn-green text-sm py-2.5"><CheckCircle2 size={15} /> Approve</button>}
                {preview.status !== "rejected" && <button onClick={() => setStatus(preview.id, "rejected")} className="h-11 px-5 rounded-md border border-red-200 text-red-600 text-sm font-semibold inline-flex items-center gap-1.5 hover:bg-red-50"><XCircle size={15} /> Reject</button>}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
